"use client"

import styled from "@emotion/styled"
import { LocalizationProvider, StaticDatePicker } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { HostServiceResponse } from "api"
import dayjs, { Dayjs } from "dayjs"
import "dayjs/locale/he"
import { useTranslations } from "next-intl"
import { useRouter } from "next/navigation"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Calendar3Icon, PriceIcon, X3Icon } from "../icons"
import ArrowDown2Icon from "../icons/ArrowDown2Icon"
import {
  StrikethroughPickersDay,
  StyledCalendarTimeRow,
  StyledConfirmButton,
  StyledDuration,
  StyledMobileCalendar,
  datePickerSlotProps,
} from "../PortFacilitiesInnerPage/CalendarWithTimeLayout"
import { formatDurationHebrew } from "../PortFacilitiesInnerPage/constants"
import InlineTimeDropdown from "../PortFacilitiesInnerPage/InlineTimeDropdown"
import ExtrasDropdown from "./ExtrasDropdown"

const StyledOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1300;
`

const StyledDrawer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background: #fff;
  border-radius: 16px 16px 0 0;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.16);
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  z-index: 1301;
`

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px;
`

const StyledTitle = styled.span`
  flex: 1;
  font-size: 20px;
  font-weight: 300;
  letter-spacing: 0.6px;
  color: #0e2846;
  text-transform: capitalize;
`

const StyledCloseButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #fff;
  border: none;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
  }
`

const StyledScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px;
`

const StyledAccordionItem = styled.div`
  border-bottom: 1px solid #e6e6e6;
`

const StyledAccordionHeader = styled.button<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px 0;
  background: transparent;
  border: none;
  cursor: pointer;

  .accordion-label {
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      width: 20px;
      height: 20px;
      color: #687079;
    }

    span {
      font-size: 16px;
      font-weight: 300;
      color: #0e2846;
      font-family: inherit;
    }
  }

  .accordion-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease;
    transform: ${({ $expanded }) => ($expanded ? "rotate(-180deg)" : "rotate(0deg)")};

    svg {
      width: 24px;
      height: 24px;
    }
  }
`

const StyledAccordionContent = styled.div`
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StyledFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #fff;
  box-shadow: 11px 0px 12.2px rgba(0, 0, 0, 0.15);
`

const StyledFooterCTA = styled.button<{ $active: boolean }>`
  flex: 1;
  height: 40px;
  border-radius: 45px;
  border: none;
  background-color: ${({ $active }) => ($active ? "#0000fa" : "#687079")};
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
`

const StyledFooterPrice = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;

  span {
    font-size: 16px;
    font-weight: 700;
    color: #0e2846;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`

const StyledTimeField = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StyledTimeLabel = styled.span`
  font-size: 16px;
  font-weight: 300;
  color: #0e2846;
  line-height: 1.5;
`

type BookingSearchParams = {
  date?: string
  startTime?: string
  endTime?: string
  numberOfPeople?: string
}


/* eslint-disable no-unused-vars */
type BookingDrawerProps = {
  open: boolean
  onClose: () => void
  price: string
  searchParams?: BookingSearchParams
  extras?: HostServiceResponse[]
  busySlots?: string[]
  selectedExtras?: Record<number, number>
  onDateChange?: (date: string) => void
  onStartTimeChange?: (time: string) => void
  onEndTimeChange?: (time: string) => void
  onExtrasChange?: (extras: Record<number, number>) => void
  priceLoading?: boolean
  hostResourceId?: number
  hostLocationName?: string
  shouldDisableDate?: (date: any) => boolean
  availableTimeSlots?: string[]
}
/* eslint-enable no-unused-vars */

export default function BookingDrawer({
  open,
  onClose,
  price,
  searchParams,
  extras,
  busySlots,
  selectedExtras = {},
  onDateChange,
  onStartTimeChange,
  onEndTimeChange,
  onExtrasChange,
  // eslint-disable-next-line no-unused-vars
  priceLoading,
  hostResourceId,
  hostLocationName,
  shouldDisableDate,
  availableTimeSlots,
}: BookingDrawerProps) {
  const t = useTranslations("portMeetingRoomProduct")
  const router = useRouter()
  const [expandedSection, setExpandedSection] = useState<string | null>(null)
  const [date, setDate] = useState(searchParams?.date || "")
  const [startTime, setStartTime] = useState(searchParams?.startTime || "")
  const [endTime, setEndTime] = useState(searchParams?.endTime || "")

  const baseSlots = useMemo(() => availableTimeSlots || [], [availableTimeSlots])

  const busySlotsSet = useMemo(
    () => new Set(busySlots || []),
    [busySlots],
  )

  const endTimeSlots = useMemo(() => {
    if (!startTime) return baseSlots
    return baseSlots.filter((slot) => slot > startTime)
  }, [startTime, baseSlots])

  const duration = startTime && endTime ? formatDurationHebrew(startTime, endTime) : ""

  const isActive = !!date && !!startTime && !!endTime

  const handleContinue = useCallback(() => {
    if (!isActive || !hostResourceId) return
    const params = new URLSearchParams()
    params.set("resourceId", String(hostResourceId))
    params.set("date", date)
    params.set("startTime", startTime)
    params.set("endTime", endTime)
    if (hostLocationName) params.set("location", hostLocationName)
    const servicesEntries = Object.entries(selectedExtras).filter(([, qty]) => qty > 0)
    if (servicesEntries.length > 0) {
      params.set("services", JSON.stringify(servicesEntries.map(([id, qty]) => ({ id: Number(id), quantity: qty }))))
    }
    router.push(`/booking?${params.toString()}`)
  }, [isActive, hostResourceId, date, startTime, endTime, hostLocationName, selectedExtras, router])

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [open])

  useEffect(() => {
    if (startTime && endTime && endTime <= startTime) {
      setEndTime("")
    }
  }, [startTime, endTime])

  if (!open) return null

  return (
    <>
      <StyledOverlay onClick={onClose} />
      <StyledDrawer>
        <StyledHeader>
          <StyledTitle>{t("orderRoom")}</StyledTitle>
          <StyledCloseButton onClick={onClose}>
            <X3Icon color="#0e2846" />
          </StyledCloseButton>
        </StyledHeader>

        <StyledScroll>
          <StyledAccordionItem>
            <StyledAccordionHeader
              $expanded={expandedSection === "date"}
              onClick={() => setExpandedSection((prev) => (prev === "date" ? null : "date"))}
            >
              <div className="accordion-arrow">
                <ArrowDown2Icon />
              </div>
              <div className="accordion-label">
                <span>{t("when")}</span>
                <Calendar3Icon color="#687079" />
              </div>
            </StyledAccordionHeader>
            {expandedSection === "date" && (
              <StyledAccordionContent>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="he">
                  <StyledMobileCalendar>
                    <StaticDatePicker
                      value={date ? dayjs(date) : null}
                      onChange={(newDate: any) => {
                        const val = newDate ? (newDate as Dayjs).format("YYYY-MM-DD") : ""
                        setDate(val)
                        onDateChange?.(val)
                      }}
                      minDate={dayjs()}
                      shouldDisableDate={shouldDisableDate}
                      slots={{
                        actionBar: () => null,
                        toolbar: () => null,
                        day: StrikethroughPickersDay,
                      }}
                      slotProps={{
                        day: {
                          sx: datePickerSlotProps.popper.sx["& .MuiPickersDay-root"],
                        },
                      }}
                    />
                    <StyledCalendarTimeRow>
                      <StyledTimeField>
                        <StyledTimeLabel>{t("startTime")}</StyledTimeLabel>
                        <InlineTimeDropdown
                          value={startTime}
                          placeholder={t("selectTime")}
                          options={baseSlots}
                          disabledOptions={busySlotsSet}
                          onChange={(val) => { setStartTime(val); onStartTimeChange?.(val) }}
                        />
                      </StyledTimeField>
                      <StyledTimeField>
                        <StyledTimeLabel>{t("endTime")}</StyledTimeLabel>
                        <InlineTimeDropdown
                          value={endTime}
                          placeholder={t("selectTime")}
                          options={endTimeSlots}
                          disabledOptions={busySlotsSet}
                          onChange={(val) => { setEndTime(val); onEndTimeChange?.(val) }}
                        />
                      </StyledTimeField>
                    </StyledCalendarTimeRow>
                    {duration && (
                      <StyledDuration
                        style={{
                          padding: "0 16px 4px",
                          textAlign: "center",
                          fontWeight: 700,
                          color: "#0e2846",
                        }}
                      >
                        {t("duration")} {duration}
                      </StyledDuration>
                    )}
                    <StyledConfirmButton onClick={() => setExpandedSection(null)}>
                      {t("confirm")}
                    </StyledConfirmButton>
                  </StyledMobileCalendar>
                </LocalizationProvider>
              </StyledAccordionContent>
            )}
          </StyledAccordionItem>

          {extras && extras.length > 0 && (
            <div style={{ padding: "0 0 16px" }}>
              <ExtrasDropdown
                extras={extras}
                selectedExtras={selectedExtras}
                onExtrasChange={(val) => onExtrasChange?.(val)}
                label={t("extras")}
              />
            </div>
          )}
        </StyledScroll>

        <StyledFooter>
          {price && (
            <StyledFooterPrice>
              <PriceIcon />
              <span>{price}</span>
            </StyledFooterPrice>
          )}
          <StyledFooterCTA $active={isActive} disabled={!isActive} onClick={handleContinue}>
            {t("continueToPayment")}
          </StyledFooterCTA>
        </StyledFooter>
      </StyledDrawer>
    </>
  )
}
